import { Box, Theme, Stack } from '@mui/material';
import { ChangeEvent, FC, ReactNode } from 'react';
import { FormProps } from '../form/helpers';
import { UiOption } from '@/lib/helpers';
import { createUseStyles } from 'react-jss';
import Chip from '@/components/chip';
import Icon from '@/components/icon';
import Menu from '@/components/menu';
import classNames from 'classnames';
import ChipSelectItem from './select-item';

interface Props extends FormProps {
  value: string;
  options: UiOption[];
  onChange: (value: string, event: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  isError?: boolean;
}

const useStyles = createUseStyles((theme: Theme) => ({
  trigger: {
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    outline: 'none',
  },
  chip: {
    maxWidth: 360,
    minWidth: 0,
    '& .MuiChip-label': {
      maxWidth: '100%',
      overflow: 'hidden',
      minWidth: 0,
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  chipDropdownIcon: {
    display: 'inline-flex',
    marginLeft: 2,
    fontSize: 18,
    color: theme.palette.darkGray,
  },
  menu: {
    padding: '4px 0',
    minWidth: 160,
  },
  readonly: {
    cursor: 'default',
  },
}));

const ChipSelect: FC<Props> = ({
  name = 'chip-select',
  value = '',
  options,
  disabled = false,
  onChange,
  size = 'medium',
  isError = false,
  readonly = false,
  placeholder = 'Select…',
}) => {
  const styles = useStyles();

  const selectedOption = options.find((opt) => opt.value === value);
  const chipLabel = selectedOption?.label || placeholder;
  const isDisabled = disabled || readonly;
  const hasOptions = !!options.length;

  const handleSelect = (optionValue: string) => {
    const syntheticEvent = {
      target: { name, value: optionValue },
    } as ChangeEvent<HTMLInputElement>;
    onChange(optionValue, syntheticEvent);
  };

  const compClass = classNames(styles.chip, { [styles.readonly]: isDisabled });

  return (
    <Menu
      trigger={
        <Chip
          className={compClass}
          label={
            hasOptions && !isDisabled ? (
              <Stack direction="row" alignItems="center" gap={0.5}>
                {chipLabel}
                <Box className={styles.chipDropdownIcon}>
                  <Icon name="menu-arrow" />
                </Box>
              </Stack>
            ) : (
              chipLabel
            )
          }
          size={size}
          color={isError ? 'error' : 'default'}
        />
      }
      disabled={isDisabled}
    >
      {options.map(
        ({ value: optValue, label: optLabel, disabled: optDisabled = false, meta = {} }) => {
          const EndAdornmentEl = meta.endAdornment as ReactNode;
          return (
            <ChipSelectItem
              key={optValue}
              optionValue={optValue}
              optionLabel={optLabel}
              endAdornment={EndAdornmentEl}
              disabled={optDisabled}
              onSelect={handleSelect}
            />
          );
        }
      )}
    </Menu>
  );
};

export default ChipSelect;
